/**
 * DestructiveActionDialog — Composant de confirmation pour actions destructives
 * 
 * Deux niveaux de protection :
 * - Standard : AlertDialog avec nom de l'élément + bouton "Supprimer"
 * - Renforcé : AlertDialog avec saisie obligatoire du nom pour confirmer
 * 
 * Usage:
 *   <DestructiveActionDialog
 *     open={!!deleteTarget}
 *     onOpenChange={() => setDeleteTarget(null)}
 *     title="Supprimer le produit"
 *     itemName="Azul"
 *     description="Le produit, ses distributions et ses éditeurs associés seront définitivement supprimés."
 *     reinforced={true}
 *     onConfirm={() => executeDelete(deleteTarget)}
 *     loading={deleting}
 *   />
 */
import React, { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertTriangle } from 'lucide-react';

export function DestructiveActionDialog({
  open = false,
  onOpenChange,
  title = 'Supprimer cet élément ?',
  itemName = '',
  description = '',
  reinforced = false,
  onConfirm,
  loading = false,
  confirmLabel = 'Supprimer définitivement',
  cancelLabel = 'Annuler',
  cascadeWarning = null,
}) {
  const [confirmText, setConfirmText] = useState('');

  // Reset confirmation text when dialog opens/closes
  useEffect(() => {
    if (!open) setConfirmText('');
  }, [open]);

  const canConfirm = reinforced
    ? confirmText.trim().toUpperCase() === (itemName || '').trim().toUpperCase()
    : true;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md" data-testid="destructive-action-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              {itemName && (
                <p className="font-medium text-gray-900">
                  {itemName}
                </p>
              )}
              {description && (
                <p className="text-muted-foreground">{description}</p>
              )}
              {cascadeWarning && (
                <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
                  <strong>Attention :</strong> {cascadeWarning}
                </div>
              )}
              <p className="text-xs text-red-600 font-medium">
                Cette action est irréversible.
              </p>
              {reinforced && itemName && (
                <div className="pt-1">
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Pour confirmer, saisissez <strong className="text-gray-900 uppercase">{itemName.toUpperCase()}</strong>
                  </p>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={itemName.toUpperCase()}
                    className="font-mono text-sm"
                    autoFocus
                    data-testid="destructive-confirm-input"
                  />
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelLabel}</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!canConfirm || loading}
            data-testid="destructive-confirm-button"
          >
            {loading ? 'Suppression...' : confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DestructiveActionDialog;
